import * as Sentry from '@sentry/react';

export type ClientEnv = {
  clerkPublishableKey: string;
  amplitudeApiKey: string;
  sentryDsn: string;
  sentryEnvironment: string;
  release: string;
  appRegistrationKey: string;
};

const raw = import.meta.env;

export const clientEnv: ClientEnv = {
  clerkPublishableKey: raw.VITE_CLERK_PUBLISHABLE_KEY ?? '',
  amplitudeApiKey: raw.VITE_AMPLITUDE_API_KEY ?? '',
  sentryDsn: raw.VITE_SENTRY_DSN ?? '',
  sentryEnvironment: raw.VITE_SENTRY_ENVIRONMENT ?? raw.MODE ?? 'development',
  release: raw.VITE_RELEASE ?? '',
  appRegistrationKey: raw.VITE_APP_REGISTRATION_KEY ?? '',
};

const reportedScopes = new Set<string>();

export function missingClientEnv(scope: 'shared' | 'learner'): string[] {
  const names: string[] = [];
  if (!clientEnv.sentryDsn) names.push('VITE_SENTRY_DSN');
  if (!clientEnv.sentryEnvironment) names.push('VITE_SENTRY_ENVIRONMENT');
  if (!clientEnv.release && import.meta.env.PROD) names.push('VITE_RELEASE');
  if (scope === 'learner') {
    if (!clientEnv.clerkPublishableKey) names.push('VITE_CLERK_PUBLISHABLE_KEY');
    if (!clientEnv.amplitudeApiKey) names.push('VITE_AMPLITUDE_API_KEY');
  }
  return names;
}

export function reportMissingClientEnv(scope: 'shared' | 'learner'): string[] {
  const missing = missingClientEnv(scope);
  if (missing.length === 0 || reportedScopes.has(scope)) return missing;
  reportedScopes.add(scope);

  if (clientEnv.sentryDsn) {
    Sentry.withScope((sentryScope) => {
      sentryScope.setLevel(scope === 'learner' ? 'error' : 'warning');
      sentryScope.setTag('area', 'configuration');
      sentryScope.setTag('platform', 'web');
      sentryScope.setFingerprint(['frontend-configuration-missing', scope, ...missing]);
      sentryScope.setExtra('missing_variables', missing);
      Sentry.captureMessage('Frontend configuration missing');
    });
  } else {
    // Sentry cannot report its own absent DSN.
    console.error(`Frontend configuration missing (${scope}): ${missing.join(', ')}`);
  }
  return missing;
}

export function resetEnvReportingForTests() {
  reportedScopes.clear();
}
